/**
 * Field schemas for different document types
 * Defines required/optional fields, display labels, and validation rules
 */

export const FIELD_SCHEMAS = {
  flight: {
    label: 'Commercial Flight',
    icon: 'plane',
    required: ['confirmation_number', 'passenger_name', 'departure_airport', 'arrival_airport', 'departure_date'],
    optional: ['airline', 'airline_code', 'flight_number', 'departure_time', 'arrival_date', 'arrival_time',
               'seat', 'class_type', 'ticket_number', 'booking_reference'],
    multiLeg: true,
    fareTerms: true, // nested fare_terms object, edited with the Fare & Bags section
    legFields: {
      required: ['departure_airport', 'arrival_airport', 'departure_date'],
      optional: ['departure_time', 'arrival_date', 'arrival_time', 'flight_number', 'seat', 'class_type', 'airline', 'airline_code']
    },
    fieldLabels: {
      confirmation_number: 'Confirmation #',
      booking_reference: 'Booking Reference',
      passenger_name: 'Passenger Name',
      traveler_name: 'Traveler Name',
      airline: 'Airline',
      airline_code: 'Airline Code',
      flight_number: 'Flight #',
      departure_airport: 'From',
      arrival_airport: 'To',
      departure_date: 'Departure Date',
      departure_time: 'Departure Time',
      arrival_date: 'Arrival Date',
      arrival_time: 'Arrival Time',
      seat: 'Seat',
      class_type: 'Class',
      cabin_class: 'Cabin Class',
      ticket_number: 'Ticket #'
    },
    fieldTypes: {
      departure_date: 'date',
      arrival_date: 'date',
      departure_time: 'time',
      arrival_time: 'time'
    }
  },

  hotel: {
    label: 'Hotel',
    icon: 'building',
    required: ['confirmation_number', 'guest_name', 'hotel_name', 'check_in_date', 'check_out_date'],
    optional: ['address', 'city', 'country', 'room_type', 'number_of_nights', 'number_of_rooms',
               'total_cost', 'currency', 'check_in_time', 'check_out_time', 'hotel_chain', 'phone', 'rate'],
    multiLeg: false,
    fieldLabels: {
      confirmation_number: 'Confirmation #',
      booking_reference: 'Booking Reference',
      guest_name: 'Guest Name',
      hotel_name: 'Hotel Name',
      property: 'Property',
      hotel_chain: 'Hotel Chain',
      address: 'Address',
      city: 'City',
      country: 'Country',
      check_in_date: 'Check-in Date',
      check_in_time: 'Check-in Time',
      check_out_date: 'Check-out Date',
      check_out_time: 'Check-out Time',
      room_type: 'Room Type',
      number_of_nights: 'Nights',
      number_of_rooms: 'Rooms',
      total_cost: 'Total Cost',
      rate: 'Rate',
      currency: 'Currency',
      phone: 'Phone'
    },
    fieldTypes: {
      check_in_date: 'date',
      check_out_date: 'date',
      check_in_time: 'time',
      check_out_time: 'time',
      number_of_nights: 'number',
      number_of_rooms: 'number'
    }
  },

  car_rental: {
    label: 'Car Rental',
    icon: 'car',
    required: ['confirmation_number', 'driver_name', 'rental_company', 'pickup_date', 'pickup_location'],
    optional: ['dropoff_date', 'dropoff_location', 'car_type', 'pickup_time', 'dropoff_time',
               'total_cost', 'currency'],
    multiLeg: false,
    fieldLabels: {
      confirmation_number: 'Confirmation #',
      booking_reference: 'Booking Reference',
      driver_name: 'Driver Name',
      traveler_name: 'Traveler Name',
      rental_company: 'Company',
      company: 'Company',
      vendor: 'Vendor',
      pickup_location: 'Pickup Location',
      pickup_address: 'Pickup Address',
      dropoff_location: 'Drop-off Location',
      return_location: 'Return Location',
      pickup_date: 'Pickup Date',
      pickup_time: 'Pickup Time',
      dropoff_date: 'Drop-off Date',
      dropoff_time: 'Drop-off Time',
      car_type: 'Car Type',
      total_cost: 'Total Cost',
      currency: 'Currency'
    },
    fieldTypes: {
      pickup_date: 'date',
      dropoff_date: 'date',
      pickup_time: 'time',
      dropoff_time: 'time'
    }
  },

  private_flight: {
    label: 'Private Flight',
    icon: 'plane-departure',
    required: ['confirmation_number', 'aircraft_type'],
    optional: ['tail_number', 'operator_name'],
    multiLeg: true,
    legFields: {
      required: ['departure_airport_icao', 'arrival_airport_icao', 'departure_date'],
      optional: ['departure_time', 'arrival_date', 'arrival_time', 'departure_fbo_name',
                 'arrival_fbo_name', 'departure_airport_name', 'arrival_airport_name', 'passengers']
    },
    fieldLabels: {
      confirmation_number: 'Trip/Reservation #',
      tail_number: 'Tail Number',
      aircraft_type: 'Aircraft Type',
      operator_name: 'Operator',
      departure_airport_icao: 'From (ICAO)',
      arrival_airport_icao: 'To (ICAO)',
      departure_airport_name: 'Departure Airport',
      arrival_airport_name: 'Arrival Airport',
      departure_date: 'Departure Date',
      departure_time: 'Departure Time',
      arrival_date: 'Arrival Date',
      arrival_time: 'Arrival Time',
      departure_fbo_name: 'Departure FBO',
      arrival_fbo_name: 'Arrival FBO',
      passengers: 'Passengers'
    },
    fieldTypes: {
      departure_date: 'date',
      arrival_date: 'date',
      departure_time: 'time',
      arrival_time: 'time'
    }
  },

  chauffeur_service: {
    label: 'Chauffeur Service',
    icon: 'car-side',
    required: ['confirmation_number', 'passenger_name', 'pickup_date', 'pickup_time', 'pickup_location'],
    optional: ['service_company', 'dropoff_location', 'vehicle_type', 'driver_name', 'driver_phone',
               'flight_info', 'special_requests', 'notes'],
    multiLeg: false,
    fieldLabels: {
      confirmation_number: 'Confirmation #',
      passenger_name: 'Passenger Name',
      service_company: 'Service Company',
      pickup_date: 'Pickup Date',
      pickup_time: 'Pickup Time',
      pickup_location: 'Pickup Location',
      dropoff_location: 'Drop-off Location',
      vehicle_type: 'Vehicle Type',
      driver_name: 'Driver Name',
      driver_phone: 'Driver Phone',
      flight_info: 'Flight Info',
      special_requests: 'Special Requests',
      notes: 'Notes'
    },
    fieldTypes: {
      pickup_date: 'date',
      pickup_time: 'time'
    }
  },

  restaurant: {
    label: 'Restaurant',
    icon: 'utensils',
    required: ['restaurant_name', 'reservation_date', 'reservation_time', 'party_size', 'guest_name'],
    optional: ['confirmation_number', 'address', 'phone', 'special_requests', 'notes'],
    multiLeg: false,
    fieldLabels: {
      restaurant_name: 'Restaurant',
      reservation_date: 'Date',
      reservation_time: 'Time',
      party_size: 'Party Size',
      guest_name: 'Guest Name',
      confirmation_number: 'Confirmation #',
      address: 'Address',
      phone: 'Phone',
      special_requests: 'Special Requests',
      notes: 'Notes'
    },
    fieldTypes: {
      reservation_date: 'date',
      reservation_time: 'time',
      party_size: 'number'
    }
  },

  speaker_meeting: {
    label: 'Speaker Meeting',
    icon: 'mic',
    required: ['program_topic', 'program_date'],
    optional: [
      'sponsor', 'product_name', 'program_id', 'program_format', 'speaker_name',
      'speaker_email', 'speaker_fee', 'program_time', 'timezone', 'venue_name',
      'venue_address', 'is_virtual', 'virtual_link', 'host_name', 'host_title',
      'host_email', 'coordinator_name', 'coordinator_email', 'speaker_bureau', 'notes'
    ],
    multiLeg: false,
    fieldLabels: {
      program_topic: 'Topic',
      program_date: 'Date',
      program_time: 'Time',
      timezone: 'Timezone',
      sponsor: 'Sponsor',
      product_name: 'Product / Brand',
      program_id: 'Program ID',
      program_format: 'Format',
      speaker_name: 'Speaker',
      speaker_email: 'Speaker Email',
      speaker_fee: 'Speaker Fee',
      venue_name: 'Venue',
      venue_address: 'Venue Address',
      is_virtual: 'Virtual',
      virtual_link: 'Virtual Link',
      host_name: 'Program Host',
      host_title: 'Host Title',
      host_email: 'Host Email',
      coordinator_name: 'Coordinator',
      coordinator_email: 'Coordinator Email',
      speaker_bureau: 'Speaker Bureau',
      notes: 'Notes'
    },
    fieldTypes: {
      program_date: 'date'
    }
  },

  // Generic occurrence: the long tail of bookings (tickets, admissions,
  // tours, registrations, classes, appointments). One schema for all of
  // them, so adding a vendor never means adding a schema here.
  occurrence: {
    label: 'Event / Booking',
    icon: 'calendar',
    required: ['title', 'start_local'],
    optional: [
      'organizer', 'confirmation_number', 'order_number', 'end_local',
      'duration_minutes', 'timezone', 'venue_name', 'address', 'is_virtual',
      'join_url', 'attendee_count', 'total_amount', 'currency', 'intent',
      'supersedes_confirmation', 'subtype_guess'
    ],
    multiLeg: false,
    fieldLabels: {
      title: 'What',
      organizer: 'Organizer / Venue',
      confirmation_number: 'Confirmation #',
      order_number: 'Order #',
      start_local: 'Starts (local time)',
      end_local: 'Ends (local time)',
      duration_minutes: 'Duration (min)',
      timezone: 'Timezone',
      venue_name: 'Venue',
      address: 'Address',
      is_virtual: 'Virtual',
      join_url: 'Join Link',
      attendee_count: 'Attendees',
      total_amount: 'Total',
      currency: 'Currency',
      intent: 'Booking / Change / Cancel',
      supersedes_confirmation: 'Replaces Confirmation #',
      subtype_guess: 'Kind'
    },
    fieldTypes: {
      start_local: 'datetime-local',
      end_local: 'datetime-local',
      duration_minutes: 'number',
      attendee_count: 'number',
      total_amount: 'number'
    }
  }
};

// event and meeting share the generic occurrence schema.
FIELD_SCHEMAS.event = FIELD_SCHEMAS.occurrence;
FIELD_SCHEMAS.meeting = FIELD_SCHEMAS.occurrence;

/**
 * Get the schema for a document type
 */
export const getSchema = (documentType) => {
  if (FIELD_SCHEMAS[documentType]) return FIELD_SCHEMAS[documentType];
  // An unknown type is NOT a flight. Falling back to the flight schema meant
  // an unrecognized item was shown and validated as a flight, so a reviewer
  // saw "Missing flight number" on a zoo ticket. The generic occurrence
  // schema asks for what any booking has.
  return FIELD_SCHEMAS.occurrence;
};

/**
 * Get all fields for a document type (required + optional)
 */
export const getAllFields = (documentType) => {
  const schema = getSchema(documentType);
  return [...schema.required, ...schema.optional];
};

/**
 * Get field label with fallback
 */
export const getFieldLabel = (documentType, fieldName) => {
  const schema = getSchema(documentType);
  return schema.fieldLabels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Check if a field is required
 */
export const isFieldRequired = (documentType, fieldName) => {
  const schema = getSchema(documentType);
  return schema.required.includes(fieldName);
};

/**
 * Get field type for input rendering
 */
export const getFieldType = (documentType, fieldName) => {
  const schema = getSchema(documentType);
  return schema.fieldTypes?.[fieldName] || 'text';
};

/**
 * Validate data against schema
 */
export const validateData = (documentType, data) => {
  const schema = getSchema(documentType);
  const errors = [];
  const missingFields = [];

  for (const field of schema.required) {
    const value = data[field];
    if (value === undefined || value === null || value === '') {
      missingFields.push(field);
      errors.push(`Missing required field: ${getFieldLabel(documentType, field)}`);
    }
  }

  // Check legs if multiLeg
  if (schema.multiLeg && data.legs && Array.isArray(data.legs)) {
    data.legs.forEach((leg, index) => {
      for (const field of schema.legFields.required) {
        const value = leg[field];
        if (value === undefined || value === null || value === '') {
          errors.push(`Leg ${index + 1}: Missing ${getFieldLabel(documentType, field)}`);
        }
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    missingFields
  };
};

/**
 * Document type options for dropdown
 */
export const DOCUMENT_TYPE_OPTIONS = [
  { value: 'flight', label: 'Commercial Flight', icon: 'plane' },
  { value: 'hotel', label: 'Hotel', icon: 'building' },
  { value: 'car_rental', label: 'Car Rental', icon: 'car' },
  { value: 'private_flight', label: 'Private Flight', icon: 'plane-departure' },
  { value: 'chauffeur_service', label: 'Chauffeur Service', icon: 'car-side' },
  { value: 'restaurant', label: 'Restaurant', icon: 'utensils' },
  { value: 'speaker_meeting', label: 'Speaker Meeting', icon: 'mic' },
  { value: 'occurrence', label: 'Event / Booking', icon: 'calendar' }
];

export default FIELD_SCHEMAS;
